import React, { useEffect, useMemo, useState } from 'react';
import styles from './OfferDetailsForm.module.css';
import { InterviewItem, OfferLetterSchema } from './types';

const PATH_SEPARATOR = '.';

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FLAT_SCHEMA_KEYS: (keyof OfferLetterSchema)[] = [
  'org_name', 'outlet_name', 'outlet_address', 'hiring_poc', 'hiring_poc_email',
  'salary_monthly', 'joining_date', 'bond_period', 'bond_clause',
];

const isFlatOfferSchema = (schema: any): schema is OfferLetterSchema =>
  schema && typeof schema === 'object' && 'org_name' in schema;

const isEmpty = (v: any): boolean =>
  v == null || v === '' || (typeof v === 'string' && v.trim() === '');

const keyToLabel = (key: string): string =>
  key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const inferFieldType = (key: string): string => {
  if (key === 'joining_date') return 'date';
  if (key === 'salary_monthly') return 'number';
  if (key === 'hiring_poc_email') return 'email';
  if (key === 'bond_clause') return 'textarea';
  return 'string';
};

const isObject = (value: any): boolean =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const toPathArray = (path: string | string[]): string[] =>
  Array.isArray(path) ? path : path.split(PATH_SEPARATOR);

const setNestedValue = (target: any, path: string | string[], value: any): any => {
  const pathArray = toPathArray(path);
  const newTarget = Array.isArray(target) ? [...target] : { ...target };
  let current = newTarget;

  for (let i = 0; i < pathArray.length - 1; i += 1) {
    const segment = pathArray[i];
    const next = current[segment];
    if (Array.isArray(next)) {
      current[segment] = [...next];
    } else if (isObject(next)) {
      current[segment] = { ...next };
    } else {
      current[segment] = {};
    }
    current = current[segment];
  }

  current[pathArray[pathArray.length - 1]] = value;
  return newTarget;
};

const getNestedValue = (target: any, path: string | string[]): any => {
  const pathArray = toPathArray(path);
  let current = target;
  for (const segment of pathArray) {
    if (!isObject(current) && !Array.isArray(current)) return undefined;
    current = current[segment];
    if (current === undefined) return undefined;
  }
  return current;
};

interface FieldDefinition {
  type?: string;
  label?: string;
  required?: boolean;
  min?: number;
  max?: number;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  disabled?: boolean;
  tooltip?: string;
  hint?: string;
  options?: { label: string; value: any }[];
  properties?: { [key: string]: FieldDefinition };
  defaultValue?: any;
  default?: any;
  fullWidth?: boolean;
  rows?: number;
  step?: number;
}

interface ExtractedField {
  name: string;
  path: string[];
  definition: FieldDefinition;
  isNested: boolean;
}

const extractFields = (
  fields: { [key: string]: FieldDefinition } | FieldDefinition[],
  parentPath: string[] = []
): ExtractedField[] => {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return [];
  }

  return Object.entries(fields).flatMap(([fieldName, definition]) => {
    const path = [...parentPath, fieldName];
    const field: ExtractedField = {
      name: fieldName,
      path,
      definition,
      isNested: parentPath.length > 0,
    };

    if (
      definition.type === 'object' &&
      definition.properties &&
      typeof definition.properties === 'object'
    ) {
      return [field, ...extractFields(definition.properties, path)];
    }

    return [field];
  });
};

const shouldAutoFillCandidateName = (fieldPath: string) =>
  ['candidateName', 'candidate_name', 'full_name'].some(
    (key) => fieldPath.endsWith(`${PATH_SEPARATOR}${key}`) || fieldPath === key
  );

const shouldAutoFillJobTitle = (fieldPath: string) =>
  ['jobTitle', 'job_title', 'position', 'role', 'designation'].some(
    (key) => fieldPath.endsWith(`${PATH_SEPARATOR}${key}`) || fieldPath === key
  );

const ensureBaseFields = (fieldsArray: ExtractedField[]): ExtractedField[] => {
  const pathJoin = (p: string[]) => p.join(PATH_SEPARATOR);
  const hasMonthlySalary = fieldsArray.some(
    (field) => pathJoin(field.path) === 'monthlySalary' || pathJoin(field.path) === 'salary_monthly'
  );

  const augmented = [...fieldsArray];

  if (!hasMonthlySalary) {
    augmented.push({
      name: 'monthlySalary',
      path: ['monthlySalary'],
      definition: {
        label: 'Monthly Salary',
        type: 'number',
        required: true,
        min: 0,
        placeholder: 'Enter monthly salary',
      },
      isNested: false,
    });
  }

  return augmented;
};

const flatSchemaToFields = (schema: OfferLetterSchema): ExtractedField[] => {
  const optionalKeys: string[] = [];
  return FLAT_SCHEMA_KEYS.map((key) => {
    const value = schema[key];
    const str = value == null ? '' : String(value);
    const isRequired = !optionalKeys.includes(key);

    return {
      name: key,
      path: [key],
      definition: {
        label: keyToLabel(key),
        type: inferFieldType(key),
        defaultValue: str,
        disabled: !isEmpty(str),
        required: isRequired,
        minDate: key === 'joining_date' ? getTodayDate() : undefined,
      },
      isNested: false,
    };
  });
};

const buildInitialValues = (
  fieldsArray: ExtractedField[],
  candidateName: string,
  defaultJobTitle: string
) => {
  let values = {};

  fieldsArray.forEach((field) => {
    const { path, definition } = field;
    const fullPath = path.join(PATH_SEPARATOR);
    let defaultValue = definition?.defaultValue ?? definition?.default ?? '';

    if (shouldAutoFillCandidateName(fullPath)) {
      defaultValue = candidateName || defaultValue || '';
    } else if (shouldAutoFillJobTitle(fullPath)) {
      defaultValue = defaultJobTitle || defaultValue || '';
    } else if (definition?.type === 'array' && !Array.isArray(defaultValue)) {
      defaultValue = Array.isArray(defaultValue) ? defaultValue : [];
    } else if (definition?.type === 'boolean') {
      defaultValue = defaultValue === true;
    }

    if (field.definition?.type === 'object' && definition?.properties) {
      // Ensure object exists even if we don't render parent directly
      values = setNestedValue(values, path, isObject(defaultValue) ? defaultValue : {});
    } else {
      values = setNestedValue(values, path, defaultValue ?? '');
    }
  });

  return values;
};

const validateValues = (values: any, fieldsArray: ExtractedField[], isBulk: boolean = false) => {
  const errors: { [key: string]: string } = {};

  fieldsArray.forEach((field) => {
    const { definition, path, name } = field;

    if (definition?.type === 'object' && definition?.properties) {
      return;
    }

    const fieldPath = path.join(PATH_SEPARATOR);

    // Skip validation for candidate name in bulk mode
    if (isBulk && ['candidateName', 'candidate_name', 'full_name'].includes(fieldPath)) {
      return;
    }

    const value = getNestedValue(values, path);
    const label = definition?.label || name.split(PATH_SEPARATOR).pop();

    if (definition?.required) {
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isEmptyObject = isObject(value) && Object.keys(value).length === 0;
      if (value === undefined || value === null || value === '' || isEmptyArray || isEmptyObject) {
        errors[fieldPath] = `${label} is required`;
        return;
      }
    }

    if (value !== undefined && value !== null && value !== '') {
      if (definition?.type === 'number') {
        const num = Number(value);
        if (Number.isNaN(num)) {
          errors[fieldPath] = `${label} must be a number`;
        } else if (definition?.min !== undefined && num < definition.min) {
          errors[fieldPath] = `${label} must be ≥ ${definition.min}`;
        } else if (definition?.max !== undefined && num > definition.max) {
          errors[fieldPath] = `${label} must be ≤ ${definition.max}`;
        }
      } else if (definition?.type === 'date') {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          errors[fieldPath] = `${label} must be a valid date`;
        } else if (definition?.minDate && date < new Date(definition.minDate)) {
          errors[fieldPath] = `${label} must be after ${definition.minDate}`;
        }
      } else if (definition?.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(String(value))) {
          errors[fieldPath] = `${label} must be a valid email`;
        }
      }
    }
  });

  return errors;
};

const sanitizeValues = (input: any): any => {
  if (Array.isArray(input)) {
    return input.map((item) => sanitizeValues(item)).filter((item) => item !== undefined);
  }

  if (isObject(input)) {
    const result: any = {};
    Object.entries(input).forEach(([key, value]) => {
      const sanitized = sanitizeValues(value);
      const isEmptyString = typeof sanitized === 'string' && sanitized.trim() === '';
      const isEmptyArray = Array.isArray(sanitized) && sanitized.length === 0;
      const isEmptyObject = isObject(sanitized) && Object.keys(sanitized).length === 0;

      if (
        sanitized !== undefined &&
        sanitized !== null &&
        !isEmptyString &&
        !isEmptyArray &&
        !isEmptyObject
      ) {
        result[key] = sanitized;
      }
    });
    return result;
  }

  return input;
};

interface OfferDetailsFormProps {
  candidates?: InterviewItem[];
  jobTitle?: string;
  schema?: any;
  onSubmit: (values: any) => void;
  onCancel: () => void;
  mode?: string;
}

const OfferDetailsForm: React.FC<OfferDetailsFormProps> = ({
  candidates = [],
  jobTitle,
  schema,
  onSubmit,
  onCancel,
  mode,
}) => {
  const firstCandidate = candidates[0] || {};

  const isBulk = candidates.length > 1;

  const fieldsArray = useMemo(() => {
    if (!schema || typeof schema !== 'object') return [];

    if (isFlatOfferSchema(schema)) {
      return ensureBaseFields(flatSchemaToFields(schema));
    }

    const fields = Array.isArray(schema.fields)
      ? schema.fields.reduce((acc: any, field: any) => {
        if (field?.name) {
          acc[field.name] = field;
        }
        return acc;
      }, {})
      : schema.fields;

    const extracted = extractFields(fields);
    return ensureBaseFields(extracted);
  }, [schema]);

  const initialValues = useMemo(
    () => {
      // Only pre-fill candidate name in single mode
      const nameToPrefill = !isBulk ? (firstCandidate.name || '') : '';
      return buildInitialValues(fieldsArray, nameToPrefill, jobTitle || '');
    },
    [fieldsArray, firstCandidate.name, jobTitle, isBulk]
  );

  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  // Pass isBulk to validateValues to skip candidate name validation
  const errors = useMemo(() => validateValues(values, fieldsArray, isBulk), [values, fieldsArray, isBulk]);
  const hasErrors = Object.keys(errors).length > 0;

  const handleFieldChange = (field: ExtractedField, rawValue: any) => {
    setValues((prev) => setNestedValue(prev, field.path, rawValue));
  };

  const handleArrayChange = (field: ExtractedField) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const arrayValue = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    handleFieldChange(field, arrayValue);
  };

  const handleBooleanChange = (field: ExtractedField) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFieldChange(field, event.target.checked);
  };

  const handleObjectChange = (field: ExtractedField) => (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (!value.trim()) {
      handleFieldChange(field, {});
      return;
    }

    try {
      const parsed = JSON.parse(value);
      handleFieldChange(field, parsed);
    } catch (error) {
      // Ignore invalid JSON, retain previous value
    }
  };

  const handlePrimitiveChange =
    (field: ExtractedField) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { value } = event.target;
        handleFieldChange(field, value);
      };

  const renderField = (field: ExtractedField) => {
    const { definition, path } = field;
    const fieldKey = path.join(PATH_SEPARATOR);
    const label =
      definition?.label ||
      path[path.length - 1].replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    const error = errors[fieldKey];
    const hint = definition?.hint;
    const isCandidateName = ['candidateName', 'candidate_name', 'full_name'].includes(fieldKey);

    // In bulk mode, hide the candidate name field entirely
    if (isBulk && isCandidateName) {
      return null;
    }

    // In single mode, disable the candidate name field (read-only)
    // Also respect definition.disabled
    const isDisabled = definition?.disabled || isCandidateName;

    const className = definition?.fullWidth ? `${styles.field} ${styles.full}` : styles.field;

    const labelContent = (
      <span className={styles.labelText} title={definition?.tooltip || undefined}>
        {label}
        {definition?.required && <span style={{ color: '#b3261e' }}> *</span>}
      </span>
    );

    if (definition?.type === 'object' && definition?.properties) {
      return null;
    }

    const value = getNestedValue(values, path);

    const labelWithTooltip = definition?.tooltip ? (
      <span className={styles.labelText}>
        {label}
        {definition?.required && <span style={{ color: '#b3261e' }}> *</span>}
        <span className={styles.tooltipIcon} title={definition.tooltip}>
          ?
        </span>
      </span>
    ) : (
      labelContent
    );

    switch (definition?.type) {
      case 'array':
        return (
          <div key={fieldKey} className={className} >
            <label htmlFor={fieldKey} className={styles.label}>
              {labelWithTooltip}
            </label>
            <input
              id={fieldKey}
              className={styles.input}
              type="text"
              value={Array.isArray(value) ? value.join(', ') : ''}
              onChange={handleArrayChange(field)}
              placeholder={definition?.placeholder || 'Enter values separated by commas'}
              disabled={isDisabled}
              data-testid={`offer-field-${fieldKey}`}
            />
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );

      case 'boolean':
        return (
          <div key={fieldKey} className={className} >
            <label
              htmlFor={fieldKey}
              className={styles.label}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <input
                id={fieldKey}
                type="checkbox"
                checked={Boolean(value)}
                onChange={handleBooleanChange(field)}
                disabled={isDisabled}
                data-testid={`offer-field-${fieldKey}`}
              />
              {labelWithTooltip}
            </label>
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );

      case 'object':
        return (
          <div key={fieldKey} className={className} >
            <label htmlFor={fieldKey} className={styles.label}>
              {labelWithTooltip}
            </label>
            <textarea
              id={fieldKey}
              className={styles.textarea}
              value={isObject(value) ? JSON.stringify(value, null, 2) : ''}
              onChange={handleObjectChange(field)}
              placeholder={definition?.placeholder || 'Enter JSON object'}
              rows={4}
              disabled={isDisabled}
              data-testid={`offer-field-${fieldKey}`}
            />
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );

      case 'select':
        return (
          <div key={fieldKey} className={className} >
            <label htmlFor={fieldKey} className={styles.label}>
              {labelWithTooltip}
            </label>
            <select
              id={fieldKey}
              className={styles.select}
              value={value || ''}
              onChange={handlePrimitiveChange(field)}
              disabled={isDisabled}
              data-testid={`offer-field-${fieldKey}`}
            >
              {definition?.placeholder && <option value="">{definition.placeholder}</option>}
              {definition?.options?.map((option) => (
                <option key={option.value ?? option.label} value={option.value}>
                  {option.label || option.value}
                </option>
              ))}
            </select>
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );

      case 'textarea':
        return (
          <div key={fieldKey} className={className} >
            <label htmlFor={fieldKey} className={styles.label}>
              {labelWithTooltip}
            </label>
            <textarea
              id={fieldKey}
              className={styles.textarea}
              value={value || ''}
              onChange={handlePrimitiveChange(field)}
              placeholder={definition?.placeholder}
              rows={definition?.rows || 4}
              disabled={isDisabled}
              data-testid={`offer-field-${fieldKey}`}
            />
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );

      case 'date': {
        const isJoiningDate = ['joiningDate', 'joining_date'].includes(fieldKey);
        const minDate = definition?.minDate || (isJoiningDate ? getTodayDate() : undefined);
        return (
          <div key={fieldKey} className={className} >
            <label htmlFor={fieldKey} className={styles.label}>
              {labelWithTooltip}
            </label>
            <input
              id={fieldKey}
              className={styles.input}
              type="date"
              value={value || ''}
              onChange={handlePrimitiveChange(field)}
              min={minDate}
              max={definition?.maxDate}
              disabled={isDisabled}
              data-testid={`offer-field-${fieldKey}`}
            />
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );
      }

      case 'number':
        return (
          <div key={fieldKey} className={className} >
            <label htmlFor={fieldKey} className={styles.label}>
              {labelWithTooltip}
            </label>
            <input
              id={fieldKey}
              className={styles.input}
              type="number"
              value={value !== undefined && value !== null ? value : ''}
              onChange={handlePrimitiveChange(field)}
              placeholder={definition?.placeholder}
              min={definition?.min}
              max={definition?.max}
              step={definition?.step}
              disabled={isDisabled}
              data-testid={`offer-field-${fieldKey}`}
            />
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );

      default:
        if (isObject(value) && !Array.isArray(value)) {
          return null;
        }

        return (
          <div key={fieldKey} className={className} >
            <label htmlFor={fieldKey} className={styles.label}>
              {labelWithTooltip}
            </label>
            <input
              id={fieldKey}
              className={styles.input}
              type={definition?.type || 'text'}
              value={value || ''}
              onChange={handlePrimitiveChange(field)}
              placeholder={definition?.placeholder}
              disabled={isDisabled}
              data-testid={`offer-field-${fieldKey}`}
            />
            {error && <div className={styles.error}>{error}</div>}
            {hint && !error && <div className={styles.hint}>{hint}</div>}
          </div>
        );
    }
  };

  if (!schema || fieldsArray.length === 0) {
    return (
      <div className={styles.form}>
        <p>No offer letter schema configured.</p>
      </div>
    );
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const validationErrors = validateValues(values, fieldsArray, isBulk);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    onSubmit(sanitizeValues(values));
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {fieldsArray.map((field) => renderField(field))}
      <div className={styles.actions}>
        <button type="button" className={styles.buttonSecondary} onClick={onCancel} data-testid="offer-details-cancel">
          Cancel
        </button>
        <button type="submit" className={styles.buttonPrimary} disabled={hasErrors} data-testid="offer-details-next">
          Next: Preview
        </button>
      </div>
    </form>
  );
};

export default OfferDetailsForm;
